import cn from "classnames"

import { Button, Checkbox, Divider, Flex, Select, Switch, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CloseOutlined } from "@/icons"
import { Form } from "@/organisms/Form"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { getRuleOption } from "@/pages/GovernancePage/applications/policies/useAiPolicies.selector"
import { CardTemplate } from "../CardTemplate"
import { RuleValueField } from "./RuleValueField"

import stylesRuleCard from "../RuleCard/RuleCard.module.scss"
import styles from "./RuleField.module.scss"

import type { RuleFieldProps } from "./RuleField.types"

export const RuleField = (props: RuleFieldProps) => {
  const { name, type, listActions } = props
  const { t } = useTranslation()
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "governance.ai.form"
  })
  const { form } = Form.useForm()
  const { ruleOptions, features } = useAiPolicies()
  const actions = Form.useWatch([type, name, "actions"], form)
  const rules = Form.useWatch([type, name, "rules"], form)
  const countByType = Form.useWatch(type, form)
  const isEmpty = !(rules ?? [])?.length
  const lastIndex = countByType?.length - 1
  const addNextConditionsList = () => listActions.add({ rules: [{}] })
  const isLastElement = name === lastIndex
  const options = ruleOptions
  const rulesFields = [{ required: true, message: t("error.requiredField") }]

  return (
    <Flex vertical gap={16}>
      <CardTemplate type={type} variant={isEmpty ? "white" : undefined}>
        <Form.Item hidden name={[name, "rules"]} valuePropName="checked" />
        {isEmpty && (
          <Flex vertical gap={8}>
            <Text>
              {type === "allow"
                ? "Define what agents are allowed to do."
                : "Define what agents cannot do."}
            </Text>
            <Button
              type="link"
              size="small"
              className={styles.buttonP0}
              onClick={addNextConditionsList}
            >
              {`+ ${type === "allow" ? "Add allow rules" : "Add don’t allow rules"}`}
            </Button>
          </Flex>
        )}
        {!isEmpty && (
          <Flex vertical gap={10} flex={1}>
            {features.enforcement && (
              <Text type="secondary" className="sm-text-ks">
                {t("governance.ai.live.form.rule.allMatch")}
              </Text>
            )}
            <Form.List name={[name, "rules"]}>
              {(fields, _actions) => {
                return (
                  <Flex vertical className={stylesRuleCard.wrapper}>
                    {fields.map(({ key, name: _name }, _index) => {
                      const currentRule = rules[_name]
                      const currentCondition = currentRule?.["condition"]
                      const conditionOption = getRuleOption(
                        options,
                        currentCondition
                      )
                      const addNextRule = () => {
                        if (!rules[_name + 1]) _actions.add()
                      }
                      const showAnd = _index !== 0 && currentCondition

                      return (
                        <Flex
                          key={key}
                          flex={1}
                          gap={8}
                          align={showAnd ? "center" : "start"}
                        >
                          <Flex vertical flex={1} gap={24}>
                            {currentCondition && (
                              <>
                                {showAnd && (
                                  <span className={stylesRuleCard.and}>
                                    AND
                                  </span>
                                )}
                                <Flex flex={1} align="start" gap={12}>
                                  <Flex gap={6}>
                                    <Text className={styles.label}>
                                      {conditionOption?.displayName}
                                    </Text>
                                    {!!conditionOption?.scope && (
                                      <Text
                                        className={styles.label}
                                        type="secondary"
                                      >
                                        {` ${tForm(`rules.scopes.${conditionOption.scope}`, "")}`}
                                      </Text>
                                    )}
                                  </Flex>
                                  <Form.Item
                                    hidden
                                    name={[_name, "condition"]}
                                  />
                                  <RuleValueField
                                    name={_name}
                                    option={conditionOption}
                                    rules={rulesFields}
                                    onChange={addNextRule}
                                  />
                                </Flex>
                              </>
                            )}
                            {!currentCondition && (
                              <Form.Item
                                name={[_name, "condition"]}
                                className={styles.f1}
                              >
                                <Select
                                  classNames={{ popup: { list: styles.list } }}
                                  placeholder="Add condition"
                                  options={options}
                                  fieldNames={{
                                    label: "displayName",
                                    value: "id"
                                  }}
                                  optionRender={(option) => {
                                    return (
                                      <Flex align="center" gap={4}>
                                        <Text>{option.label}</Text>
                                        {"scope" in option.data && (
                                          <Text type="secondary">
                                            {` ${tForm(`rules.scopes.${option.data.scope}`, "")}`}
                                          </Text>
                                        )}
                                      </Flex>
                                    )
                                  }}
                                />
                              </Form.Item>
                            )}
                          </Flex>
                          <Button
                            type="text"
                            disabled={!currentCondition}
                            icon={
                              <Text type="secondary" className="sm-text-ks">
                                <CloseOutlined />
                              </Text>
                            }
                            key={`${showAnd}`}
                            className={cn(showAnd && styles.removeBtn)}
                            onClick={() => {
                              const lastCondition =
                                rules[rules.length - 1]?.condition
                              if (fields.length === 1) listActions.remove(name)
                              else if (fields.length === 2 && !lastCondition)
                                listActions.remove(name)
                              else {
                                _actions.remove(_name)

                                if (lastCondition) _actions.add({})
                              }
                            }}
                          />
                        </Flex>
                      )
                    })}
                  </Flex>
                )
              }}
            </Form.List>

            {!features.ruleActions && (
              <Flex flex={1} align="center" justify="end">
                <Button
                  type="link"
                  size="small"
                  className={styles.buttonP0}
                  onClick={() => listActions.remove(name)}
                >
                  {t("governance.ai.live.form.rule.delete")}
                </Button>
              </Flex>
            )}
            {features.ruleActions && (
              <Flex flex={1} align="center" justify="space-between">
                <Form.Item
                  hidden
                  name={[name, "actions"]}
                  valuePropName="checked"
                />
                <Flex gap={10} align="center">
                  <Form.Item noStyle name={[name, "actions", "enabled"]}>
                    <Switch size="small" checked={actions?.enabled} />
                  </Form.Item>
                  <Text>Add actions upon violation</Text>
                </Flex>
                <Button
                  type="link"
                  size="small"
                  className={styles.buttonP0}
                  onClick={() => listActions.remove(name)}
                >
                  {t("governance.ai.live.form.rule.delete")}
                </Button>
              </Flex>
            )}
            {features.ruleActions && actions?.enabled && (
              <Flex vertical gap={4}>
                <Form.Item
                  noStyle
                  name={[name, "actions", "enforceRuleOnRuntime"]}
                  valuePropName="checked"
                >
                  <Checkbox value={actions?.enforceRuleOnRuntime}>
                    Enforce rule on runtime (Block session)
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  name={[name, "actions", "preventViolationOnDevelopment"]}
                  valuePropName="checked"
                >
                  <Checkbox value={actions?.preventViolationOnDevelopment}>
                    Prevent on violation on development
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  name={[name, "actions", "notifyOnViolation"]}
                  valuePropName="checked"
                >
                  <Checkbox value={actions?.notifyOnViolation}>
                    Notify when violation occur
                  </Checkbox>
                </Form.Item>
              </Flex>
            )}
          </Flex>
        )}
      </CardTemplate>
      {name < lastIndex && (
        <Flex align="center" gap={12}>
          <Flex flex={1}>
            <Divider className={styles.divider14} />
          </Flex>
          <Text type="secondary" className={cn("sm-text-ks", styles.mono)}>
            OR
          </Text>
          <Flex flex={1}>
            <Divider className={styles.divider14} />
          </Flex>
        </Flex>
      )}
      {isLastElement && rules?.length > 0 && (
        <span>
          <Button
            color="primary"
            variant="dashed"
            className={styles.buttonP0}
            onClick={addNextConditionsList}
          >
            + Another OR condition
          </Button>
        </span>
      )}
    </Flex>
  )
}
